<template>
  <div class="container-fluid mt-5">
    <div>
      <card
        class="no-binvoice-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-8">
              <h3 class="mb-0 view-header-buttons">
                {{ resellerInvoice.code }}
                <base-button
                  type="primary"
                  icon
                  size="sm"
                  @click="editResellerInvoice()"
                  v-if="resellerInvoice.status === INVOICE_STATUS_DRAFT"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    resellerInvoice.status === INVOICE_STATUS_DRAFT &&
                    $currentUserCan($permissions.PERM_DELETE_RESELLER_INVOICES)
                  "
                  type="danger"
                  icon
                  size="sm"
                  @click="deleteResellerInvoice()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>

                <base-button
                  type="primary"
                  icon
                  size="sm"
                  @click="validateResellerInvoice()"
                  :disabled="loading"
                  v-if="
                    resellerInvoice.items.length > 0 &&
                    resellerInvoice.status === INVOICE_STATUS_DRAFT
                  "
                >
                  <span class="btn-inner--icon">
                    <i class="ni ni-check-bold"></i>
                    {{ $t("RESELLER_INVOICES.VALIDATE") }}
                  </span>
                </base-button>

                <base-button
                  v-if="resellerInvoice.status === INVOICE_STATUS_VALIDATED"
                  type="primary"
                  icon
                  size="sm"
                  @click="addResellerPayment()"
                >
                  <span class="btn-inner--icon">
                    <i class="ni ni-money-coins"></i>
                    {{ $t("RESELLER_INVOICES.ADD_PAYMENT") }}
                  </span>
                </base-button>

                <base-button
                  type="primary"
                  icon
                  size="sm"
                  @click="viewResellerOrder()"
                  :disabled="loading"
                  v-if="resellerInvoice.resellerOrder"
                >
                  <span class="btn-inner--icon">
                    <i class="ni ni-bag-17"></i>
                    {{ $t("RESELLER_INVOICES.VIEW_ORDER") }}
                  </span>
                </base-button>

                <base-button
                  type="danger"
                  icon
                  size="sm"
                  @click="cancelResellerInvoice()"
                  :disabled="loading"
                  v-if="canCancelResellerInvoice"
                >
                  <span class="btn-inner--icon">
                    <i class="ni ni-fat-remove"></i>
                    {{ $t("RESELLER_INVOICES.CANCEL") }}
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>

              <notification-subscription
                :objectType="'reseller-invoices'"
                :objectId="resellerInvoice.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
            </div>
          </div>
        </template>
        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <resellerInvoice-view-global
                    :resellerInvoice="resellerInvoice"
                  />
                </tab-pane>

                <tab-pane title="details" id="2" :active="false">
                  <span slot="title">
                    <i class="ni ni-bullet-list-67" />
                    {{ $t("COMMON.DETAILS") }}
                  </span>
                  <resellerInvoice-view-details
                    :resellerInvoice="resellerInvoice"
                    @resellerInvoiceItemsUpdated="get"
                  />
                </tab-pane>

                <tab-pane
                  title="payments"
                  id="3"
                  :active="false"
                  v-if="
                    $currentUserCan(
                      $permissions.PERM_VIEW_ANY_RESELLER_PAYMENTS
                    )
                  "
                >
                  <span slot="title">
                    <i class="ni ni-money-coins" />
                    {{ $t("COMMON.PAYMENTS") }}
                  </span>
                  <reseller-invoice-view-payments
                    :resellerInvoice="resellerInvoice"
                  />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <resellerInvoice-view-logs
                    :resellerInvoice="resellerInvoice"
                  />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import swal from "sweetalert2";
import {
  INVOICE_STATUS_CANCELED,
  INVOICE_STATUS_DRAFT,
  INVOICE_STATUS_VALIDATED,
} from "@/constants/invoices";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { Tabs, TabPane } from "@/components";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import ResellerInvoiceViewGlobal from "./partials/ResellerInvoiceViewGlobal.vue";
import ResellerInvoiceViewDetails from "./partials/ResellerInvoiceViewDetails.vue";
import ResellerInvoiceViewLogs from "./partials/ResellerInvoiceViewLogs.vue";
import defaultResellerInvoice from "./defaultResellerInvoice";
import ResellerInvoiceViewPayments from "./partials/ResellerInvoiceViewPayments.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    ResellerInvoiceViewGlobal,
    ResellerInvoiceViewDetails,
    ResellerInvoiceViewLogs,
    NotificationSubscription,
    ResellerInvoiceViewPayments,
  },

  mixins: [requestErrorMixin],

  data() {
    return {
      loading: false,
      resellerInvoice: cloneDeep(defaultResellerInvoice),
      INVOICE_STATUS_DRAFT: INVOICE_STATUS_DRAFT,
      INVOICE_STATUS_CANCELED: INVOICE_STATUS_CANCELED,
      INVOICE_STATUS_VALIDATED: INVOICE_STATUS_VALIDATED,
    };
  },

  computed: {
    canCancelResellerInvoice() {
      if (
        this.resellerInvoice.status === INVOICE_STATUS_CANCELED ||
        this.resellerInvoice.status === INVOICE_STATUS_DRAFT
      ) {
        return false;
      }
      if (this.resellerInvoice.status === INVOICE_STATUS_VALIDATED) {
        return true;
      }
      return false;
    },
  },

  watch: {},

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("resellerInvoices/get", id);
        this.resellerInvoice =
          this.$store.getters["resellerInvoices/resellerInvoice"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editResellerInvoice(row) {
      this.$router.push({
        name: "Edit ResellerInvoice",
        params: { id: this.resellerInvoice.id },
      });
    },

    async validateResellerInvoice() {
      try {
        this.loading = true;
        await this.$store.dispatch(
          "resellerInvoices/validate",
          this.resellerInvoice.id
        );
        this.resellerInvoice =
          this.$store.getters["resellerInvoices/resellerInvoice"];
        this.$notify({
          type: "success",
          message: this.$t("RESELLER_INVOICES.RESELLER_INVOICE_VALIDATED"),
        });
        this.loading = false;
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async cancelResellerInvoice() {
      const confirmation = await swal.fire({
        title: this.$t("RESELLER_INVOICES.CANCEL_RESELLER_INVOICE_QUESTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) {
        return;
      }

      try {
        this.loading = true;
        await this.$store.dispatch(
          "resellerInvoices/cancel",
          this.resellerInvoice.id
        );
        this.resellerInvoice =
          this.$store.getters["resellerInvoices/resellerInvoice"];
        this.$notify({
          type: "success",
          message: this.$t("RESELLER_INVOICES.RESELLER_INVOICE_CANCELED"),
        });
        this.loading = false;
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async deleteResellerInvoice() {
      const confirmation = await swal.fire({
        title: this.$t("RESELLER_INVOICES.DELETE_THIS_RESELLER_INVOICE"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "resellerInvoices/destroy",
            this.resellerInvoice.id
          );
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("RESELLER_INVOICES.RESELLER_INVOICE_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async viewResellerOrder() {
      this.$router.push(
        this.$objectViewRoute(this.resellerInvoice.resellerOrder)
      );
    },

    async addResellerPayment(row) {
      this.$router.push({
        name: "Add ResellerPayment",
        query: {
          invoice_id: this.resellerInvoice.id,
          amount: this.resellerInvoice.total_remaining_payment.toFixed(2),
        },
      });
    },

    goBack() {
      this.$router.push({ name: "List ResellerInvoices" });
    },
  },
};
</script>
